import { MappingsRow } from '@components/Forms';
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, Box, Button, Divider, TextField } from '@mui/material';
import { useLowResolution } from 'hooks';
import { uniqueId } from 'lodash';
import React, { useCallback } from 'react';
import { IRole } from 'types/authorization';
import { ISSOGroupMappingsItem } from 'types/settings';

interface IGenericMappingsProps {
	id: string;
	formik: any;
	roles: IRole[];
}

const SSOMappings: React.FC<IGenericMappingsProps> = ({
	formik,
	id,
	roles
}) => {
	const mappings = React.useMemo<ISSOGroupMappingsItem[]>(
		() => formik.values[id] || [],
		[formik.values, id]
	);

	const handleAddMappings = useCallback(async () => {
		await formik.setFieldValue(id, [
			...mappings,
			{
				role: roles.length > 0 ? roles[0] : { id: '', name: '' },
				groups: [],
				id: uniqueId()
			}
		]);
	}, [formik, id, mappings, roles]);

	const handleSetRole = useCallback(
		async (index: number, role: { name: string; id: string }) => {
			const newMappings = mappings.map((mapping, i) => {
				if (i === index) {
					return {
						...mapping,
						role
					};
				}
				return mapping;
			});
			await formik.setFieldValue(id, newMappings);
		},
		[formik, id, mappings]
	);

	const handleSetGroups = useCallback(
		async (index: number, groups: string[]) => {
			const newMappings = mappings.map((mapping, i) => {
				if (i === index) {
					return {
						...mapping,
						groups
					};
				}
				return mapping;
			});
			await formik.setFieldValue(id, newMappings);
		},
		[formik, id, mappings]
	);

	const handleDeleteMappings = useCallback(
		(index: number) => () => {
			const newMappings = mappings.filter((_, i) => i !== index);
			formik.setFieldValue(id, newMappings);
		},
		[formik, id, mappings]
	);

	const lowRes = useLowResolution();

	return (
		<>
			{mappings.map((mapping, index: number) => (
				<Box pt={2} key={mapping.id}>
					<MappingsRow
						label={intl.tr('authMethods.SSO.user.mapping.attributes.mapTo')}
						leftComponent={
							<Autocomplete
								filterSelectedOptions
								data-testid={`groups-sso-mappings-autocomplete-${index}`}
								fullWidth
								id={`groups-sso-mappings-autocomplete-${index}`}
								multiple
								freeSolo
								options={mapping.groups || []}
								value={mapping.groups || []}
								autoSelect
								onChange={async (_, v: string[]) => {
									await handleSetGroups(index, v || []);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label={intl.tr(
											'tenants.sso.mappings.groups.dialog.input.groups.label'
										)}
										fullWidth
									/>
								)}
							/>
						}
						rightComponent={
							<Autocomplete
								data-testid="groups-sso-mappings-roles-autocomplete"
								value={mapping.role}
								getOptionLabel={(option) => option?.name}
								blurOnSelect
								isOptionEqualToValue={(option, value) => option.id === value.id}
								options={roles.map((role) => ({
									id: role.id,
									name: role.name
								}))}
								onChange={(_, v: any) => {
									handleSetRole(index, v as { name: string; id: string });
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label={intl.tr(
											'tenants.sso.mappings.groups.dialog.input.role.label'
										)}
										fullWidth
									/>
								)}
							/>
						}
						onDelete={handleDeleteMappings(index)}
					/>
					{lowRes && <Divider />}
				</Box>
			))}
			<Box my={2}>
				<Button
					color="primary"
					startIcon={<AddIcon />}
					onClick={handleAddMappings}
					data-testid="groups-sso-mappings-add-button"
				>
					{intl.tr('tenants.sso.mappings.groups.button.label')}
				</Button>
			</Box>
		</>
	);
};

export default SSOMappings;
